import { api } from '@/api/http';
import { ROUTES } from '@/app/routes';
import FadeIn from '@/components/motion/fade-in';
import logoPng from '@/assets/logo.png';
import LoadingWrapper from '@/components/loading-wrapper';
import AuthPinButton from '@/components/ui/auth-pin-button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { $setFeatures } from '@/entities/features-entity';
import { $nickname, $setProfile } from '@/entities/profile-entity';
import { useUnit } from 'effector-react';
import { DeleteIcon, UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const TEAM_LOGO = import.meta.env.VITE_TEAM === 'hell' ? { img: logoPng, width: '87%' } : { img: logoPng, width: '50%' };

export default function AuthPage() {
	const [pin, setPin] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [nickname] = useUnit([$nickname]);
	const navigate = useNavigate();

	const [setProfile, setFeatures] = useUnit([$setProfile, $setFeatures]);

	useEffect(() => {
		if (pin.length < 4) return;
		queueMicrotask(() => setIsLoading(true));

		api
			.post('/auth', { pin, nickname: nickname })
			.then(res => {
				setProfile(res.data.profile);
				if (res.data.features) {
					setFeatures(res.data.features);
				}
				navigate(ROUTES.profile);
			})
			.catch(() => {
				setIsLoading(false);
				setPin('');
			});
	}, [pin, nickname]);

	useEffect(() => {
		const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
		function handleKeyDown(e: KeyboardEvent) {
			if (isLoading) return;
			if (keys.includes(e.key)) {
				setPin(v => (v.length < 4 ? v + keys[keys.indexOf(e.key)] : v));
			} else if (e.key === 'Backspace') {
				setPin(v => v.slice(0, -1));
			}
		}
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [pin, isLoading]);

	// w-[50%]
	return (
		<main className=''>
			<FadeIn>
				<img src={TEAM_LOGO.img} className='mx-auto mt-[30px] select-none' style={{ width: TEAM_LOGO.width }} />
				<h1 className='text-md text-muted-foreground flex items-center gap-1 justify-center mb-2'>
					<UserIcon size={20} />
					{nickname}
				</h1>
			</FadeIn>
			<LoadingWrapper show={isLoading}>
				<FadeIn delay={0.03}>
				<div className='w-full justify-center flex flex-col items-center gap-7'>
					<InputOTP className='' maxLength={4} value={pin} onChange={v => setPin(v)}>
						<InputOTPGroup className=''>
							<InputOTPSlot
								className='h-[50px] text-xl'
								index={0}
								style={{
									width: pin.length === 0 ? '70px' : '50px',
								}}
							/>
							<InputOTPSlot
								className='h-[50px] text-xl'
								index={1}
								style={{
									width: pin.length === 1 ? '70px' : '50px',
								}}
							/>
							<InputOTPSlot
								className='h-[50px] text-xl'
								index={2}
								style={{
									width: pin.length === 2 ? '70px' : '50px',
								}}
							/>
							<InputOTPSlot
								className='h-[50px] text-xl'
								index={3}
								style={{
									width: pin.length === 3 ? '70px' : '50px',
								}}
							/>
						</InputOTPGroup>
					</InputOTP>
					<div className='flex flex-col gap-[7px]'>
						<div className='flex gap-[7px]'>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '1' : v))}>1</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '2' : v))}>2</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '3' : v))}>3</AuthPinButton>
						</div>
						<div className='flex gap-[7px]'>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '4' : v))}>4</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '5' : v))}>5</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '6' : v))}>6</AuthPinButton>
						</div>
						<div className='flex gap-[7px]'>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '7' : v))}>7</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '8' : v))}>8</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '9' : v))}>9</AuthPinButton>
						</div>
						<div className='flex gap-[7px]'>
							<div className='w-[60px] h-[60px]'></div>
							<AuthPinButton onClick={() => setPin(v => (v.length < 4 ? v + '0' : v))}>0</AuthPinButton>
							<AuthPinButton onClick={() => setPin(v => v.slice(0, v.length - 1))}>
								<DeleteIcon />
							</AuthPinButton>
						</div>
					</div>
				</div>
				</FadeIn>
			</LoadingWrapper>
		</main>
	);
}
